import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import { PdfReportDisplay } from "@/types/affiliation";
import {
  ConductTestInfo,
  ConductTestState,
  ConductTestStore,
  TestTimeStamps,
} from "@/types/conductTest";
import { ActivityEvent, ApiResponse, IdNamePair, InputOption } from "@/types/global";
import {
  AffiliationTestReport,
  AffiliationTestReportDetails,
  AllInstituteTestStudentMarks,
  BatchInstituteTestSummary,
  InstituteTestAffiliation,
  InstituteTestDesign,
  InstituteTestStudent,
  InstituteTestTemplate,
  ManualMarksData,
  PastYearPaper,
  PastYearPaperInputs,
  PastYearPaperUpdate,
  PastYearQuestionPaper,
  PastYearQuestionPaperInputs,
  PastYearQuestionPaperUpdate,
  PreloadedTestInfo,
  StudentInstituteTestSummary,
} from "@/types/instituteTest";
import {
  OmrReaderConfig,
  OmrTemplate,
  QuestionPaperTemplate,
  StudentOmrResult,
} from "@/types/offlineTest";
import { SectionGroup, SelectorQuestion } from "@/types/questionSelector";
import { SelfTestCreate, SelfTestDisplay, SelfTestTemplate } from "@/types/selfTest";
import { TestReport, TestReportDetails } from "@/types/studentTestReport";
import {
  InstituteTestTemplateCreate,
  TestPaperTemplateInputs,
  TestSeries,
  TestSeriesBatch,
  TestSeriesInputs,
  TestSeriesUpdate,
} from "@/types/testSeries";

export default class TestApi extends ApiVerified {
  async getSelfTestTemplates(courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SelfTestTemplate>>(
        `self-test/get-test-templates`,
        { params: { courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createSelfTest(testData: SelfTestCreate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(`self-test/create-test`, testData);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSelfTestsForStudent(userId: number, category: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<SelfTestDisplay>(
        `self-test/get-tests-for-student`,
        { params: { userId: userId, category: category } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async loadTest(testId: number, testType: string, userId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ConductTestStore>(`conduct-test/load-test`, {
        params: { testId: testId, testType: testType, userId: userId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async startTest(
    testInstanceId: number,
    testType: string,
    duration: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<TestTimeStamps>(`conduct-test/start-test`, {
        testInstanceId: testInstanceId,
        testType: testType,
        duration: duration,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async pushUnsavedTestEvents(
    testId: number,
    testInstanceId: number,
    testType: string,
    testEvents: Array<ActivityEvent>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`conduct-test/push-test-events`, {
        testId: testId,
        testInstanceId: testInstanceId,
        testType: testType,
        testEvents: testEvents,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async uploadSubjectiveTestAnswer(
    testId: number,
    testInstanceId: number,
    testType: string,
    questionId: number,
    answerBase64: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(
        `conduct-test/upload-subjective-test-answer`,
        {
          testId: testId,
          testInstanceId: testInstanceId,
          testType: testType,
          questionId: questionId,
          answerBase64: answerBase64,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async endTest(testInfo: ConductTestInfo, testState: ConductTestState): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`conduct-test/end-test`, {
        TestInfo: testInfo,
        TestState: testState,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentTestReport(
    testInstanceId: number,
    testType: string,
    userId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<TestReport>(
        `conduct-test/student-test-report`,
        {
          params: { testInstanceId: testInstanceId, testType: testType, userId: userId },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentTestReportDetails(
    testInstanceId: number,
    testType: string,
    userId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<TestReportDetails>(
        `conduct-test/student-test-report-details`,
        {
          params: { testInstanceId: testInstanceId, testType: testType, userId: userId },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async resetStudentTestAttempts(
    testId: number,
    testType: string,
    studentId: number,
    affiliationId: number,
    userId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(`conduct-test/reset-student-test`, {
        params: {
          testId: testId,
          testType: testType,
          studentId: studentId,
          affiliationId: affiliationId,
          userId: userId,
        },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async resetAllTestAttempts(
    testId: number,
    testType: string,
    affiliationId: number,
    batchId: number,
    userId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `conduct-test/reset-all-test-attempts`,
        {
          params: {
            testId: testId,
            testType: testType,
            affiliationId: affiliationId,
            batchId: batchId,
            userId: userId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAvailableTestQuestionsForChapter(
    courseChapterId: number,
    questionTypeId: number,
    onlyActive: boolean,
    exclusionBatchIdsCsv: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<SelectorQuestion>>(
        `institute-test/get-available-questions-for-chapter`,
        {
          courseChapterId: courseChapterId,
          questionTypeId: questionTypeId,
          onlyActive: onlyActive,
          exclusionBatchIdsCsv: exclusionBatchIdsCsv,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTestAnswerKeyPdf(
    instituteTestId: number,
    affiliationId: number,
    ignoreTestAccess: boolean = false,
  ) {
    try {
      const response = await this.axiosInstance.get<string>(
        `institute-test/get-test-answer-key-pdf`,
        {
          params: {
            instituteTestId: instituteTestId,
            affiliationId: affiliationId,
            ignoreTestAccess: ignoreTestAccess,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTestSolutionsPdf(
    instituteTestId: number,
    affiliationId: number,
    ignoreTestAccess: boolean = false,
  ) {
    try {
      const response = await this.axiosInstance.get<string>(
        `institute-test/get-test-solutions-pdf`,
        {
          params: {
            instituteTestId: instituteTestId,
            affiliationId: affiliationId,
            ignoreTestAccess: ignoreTestAccess,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async generateTestQuestionPdf(
    instituteTestId: number,
    affiliationId: number,
    templateName: string,
    groupQuestionTypes: boolean,
    forceRegenerate: boolean,
    ignoreTestAccess: boolean = false,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<string>(
        `institute-test/generate-test-question-pdf`,
        {
          params: {
            instituteTestId: instituteTestId,
            affiliationId: affiliationId,
            templateName: templateName,
            groupQuestionTypes: groupQuestionTypes,
            forceRegenerate: forceRegenerate,
            ignoreTestAccess: ignoreTestAccess,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getOmrTemplatesForTest(
    instituteTestId: number,
    numericalAsInteger: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<OmrTemplate>>(
        `institute-test/get-test-omr-templates`,
        { params: { instituteTestId: instituteTestId, numericalAsInteger: numericalAsInteger } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async generateTestOmrImage(
    instituteTestId: number,
    batchId: number,
    groupQuestionTypes: boolean,
    numericalAsInteger: boolean,
    templateName: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(
        `institute-test/generate-test-omr-image`,
        {
          instituteTestId: instituteTestId,
          batchId: batchId,
          groupQuestionTypes: groupQuestionTypes,
          numericalAsInteger: numericalAsInteger,
          templateName: templateName,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getOfflineTestOmrImage(instituteTestId: number, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<string>(
        `institute-test/get-offline-test-omr-image`,
        { params: { instituteTestId: instituteTestId, batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getInstituteTestsForBatch(batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `institute-test/get-tests-for-batch`,
        { params: { batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveInstituteTestTemplate(
    instituteTestTemplate: InstituteTestTemplate,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`institute-test/save-test-template`, {
        testTemplate: instituteTestTemplate,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getInstituteTestDesign(
    instituteTestId: number,
    affiliationId: number,
    ignoreTestAccess: boolean = false,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<InstituteTestDesign>(
        `institute-test/get-test-design`,
        {
          params: {
            instituteTestId: instituteTestId,
            affiliationId: affiliationId,
            ignoreTestAccess: ignoreTestAccess,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getInstituteTestInstructions(instituteTestId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<InstituteTestDesign>(
        `institute-test/get-test-instructions`,
        { params: { instituteTestId: instituteTestId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveInstituteTestInstructions(
    instituteTestId: number,
    affiliationId: number,
    testInstructions: Array<string>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/save-test-instructions`,
        {
          instituteTestId: instituteTestId,
          affiliationId: affiliationId,
          testInstructions: testInstructions,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getInstituteTestsForAffiliation(
    affiliationId: number,
    category: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<InstituteTestAffiliation>>(
        `institute-test/get-tests-for-affiliation`,
        {
          params: {
            affiliationId: affiliationId,
            category: category,
            pageNumber: pageNumber,
            pageSize: pageSize,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getInstituteTestsByTag(
    testTagId: number,
    courseId: number,
    testSeriesName: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `institute-test/get-tests-by-tag`,
        { params: { testTagId: testTagId, courseId: courseId, testSeriesName: testSeriesName } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedInstituteTestsForStudent(
    userId: number,
    category: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<InstituteTestStudent>(
        `institute-test/get-detailed-tests-for-student`,
        { params: { userId: userId, category: category } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getInstituteTestsForStudent(userId: number, onlyCompleted: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `institute-test/get-tests-for-student`,
        { params: { userId: userId, onlyCompleted: onlyCompleted } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteInstituteTestTemplate(
    mongoId: string,
    courseId: number,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `institute-test/delete-test-template`,
        { params: { mongoId: mongoId, courseId: courseId, affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createInstituteTest(
    testDesign: InstituteTestDesign,
    affiliationId: number,
    courseId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/create-institute-test`,
        {
          testDesign: testDesign,
          affiliationId: affiliationId,
          courseId: courseId,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async rescheduleInstituteTest(
    instituteTestId: number,
    batchIds: Array<number>,
    startDatetime: string,
    endDatetime: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/reschedule-institute-test`,
        {
          instituteTestId: instituteTestId,
          batchIds: batchIds,
          startDatetime: startDatetime,
          endDatetime: endDatetime,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async cloneInstituteTest(
    instituteTestId: number,
    testName: string,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/clone-institute-test`,
        { instituteTestId: instituteTestId, testName: testName, affiliationId: affiliationId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async publishInstituteTest(
    instituteTestId: number,
    affiliationId: number,
    batchIds: Array<number>,
    startDatetime: string,
    endDatetime: string,
    onlyOffline: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/publish-institute-test`,
        {
          instituteTestId: instituteTestId,
          affiliationId: affiliationId,
          batchIds: batchIds,
          startDatetime: startDatetime,
          endDatetime: endDatetime,
          onlyOffline: onlyOffline,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async unpublishInstituteTest(
    instituteTestId: number,
    batchIds: Array<number>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `institute-test/unpublish-institute-test`,
        { instituteTestId: instituteTestId, batchIds: batchIds },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteInstituteTest(
    instituteTestId: number,
    affiliationId: number,
    userId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `institute-test/delete-institute-test`,
        {
          params: {
            instituteTestId: instituteTestId,
            affiliationId: affiliationId,
            userId: userId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async renameInstituteTest(
    instituteTestId: number,
    affiliationId: number,
    testName: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/rename-institute-test`,
        {
          instituteTestId: instituteTestId,
          affiliationId: affiliationId,
          testName: testName,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveTestQuestions(
    instituteTestId: number,
    affiliationId: number,
    sectionGroups: Array<SectionGroup>,
    finalizeTest: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/save-test-questions`,
        {
          instituteTestId: instituteTestId,
          affiliationId: affiliationId,
          sectionGroups: sectionGroups,
          finalizeTest: finalizeTest,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async generateOfflineTestReport(
    testName: string,
    academicSession: string,
    csvFile: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(
        `pdf-report/offline-test-subject-wise-report`,
        { testName: testName, academicSession: academicSession, csvFile: csvFile },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async submitOmrsForProcessing(
    instituteTestId: number,
    batchId: number,
    templateName: string,
    fileName: string,
    fileSource: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/submit-omrs-for-processing`,
        {
          instituteTestId: instituteTestId,
          batchId: batchId,
          templateName: templateName,
          fileName: fileName,
          fileSource: fileSource,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async submitAndProcessStudentOmr(
    instituteTestId: number,
    batchId: number,
    userId: number,
    userName: string,
    rollNumber: number,
    templateName: string,
    fileSource: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<StudentOmrResult>(
        `institute-test/submit-process-student-omr`,
        {
          instituteTestId: instituteTestId,
          batchId: batchId,
          userId: userId,
          userName: userName,
          rollNumber: rollNumber,
          templateName: templateName,
          fileSource: fileSource,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getRollNumberWiseStudentOmrResults(
    instituteTestId: number,
    batchId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudentOmrResult>>(
        `institute-test/get-roll-number-wise-student-omr-results`,
        { params: { instituteTestId: instituteTestId, batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getOmrUploadsByStatus(
    omrUploadStatusId: number,
    minDate: string,
    maxDate: string,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get(`institute-test/get-omr-uploads-by-status`, {
        params: {
          omrUploadStatusId: omrUploadStatusId,
          minDate: minDate,
          maxDate: maxDate,
          affiliationId: affiliationId,
        },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getOmrReaderConfig(instituteTestId: number, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<OmrReaderConfig>(
        `institute-test/get-omr-reader-config`,
        { params: { instituteTestId: instituteTestId, batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async checkOfflineTestReviewAccess(
    instituteTestId: number,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<boolean>(
        `institute-test/check-offline-test-review-access`,
        { params: { instituteTestId: instituteTestId, affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveEditedOmrResults(omrResults: Array<StudentOmrResult>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/save-edited-omr-results`,
        omrResults,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async finalizeOmrResults(instituteTestId: number, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/finalize-omr-results`,
        { instituteTestId: instituteTestId, batchId: batchId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async reprocessOmrUpload(mongoId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/reprocess-omr-upload`,
        { mongoId: mongoId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteOmrUpload(mongoId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`institute-test/delete-omr-upload`, {
        mongoId: mongoId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQuestionPaperTemplates(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<QuestionPaperTemplate>>(
        `institute-test/get-question-paper-templates`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationTestReport(
    testId: number,
    testType: string,
    affiliationId: number,
    userId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationTestReport>>(
        `conduct-test/affiliation-test-report`,
        {
          params: {
            testId: testId,
            testType: testType,
            affiliationId: affiliationId,
            userId: userId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationTestReportDetails(
    testId: number,
    testType: string,
    affiliationId: number,
    userId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationTestReportDetails>>(
        `conduct-test/affiliation-test-report-details`,
        {
          params: {
            testId: testId,
            testType: testType,
            affiliationId: affiliationId,
            userId: userId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTestManualMarksData(instituteTestId: number, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ManualMarksData>(
        `institute-test/get-test-manual-marks-data`,
        { params: { instituteTestId: instituteTestId, batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveTestManualMarksData(manualMarksData: ManualMarksData): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/save-test-manual-marks-data`,
        { manualMarksData: manualMarksData },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPreloadedTestsForAdmin(courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PreloadedTestInfo>>(
        `institute-test/preloaded-tests-for-admin`,
        { params: { courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPreloadedTestsForAffiliation(
    courseId: number,
    isParentAffiliation: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PreloadedTestInfo>>(
        `institute-test/preloaded-tests-for-affiliation`,
        { params: { courseId: courseId, isParentAffiliation: isParentAffiliation } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPreloadedTestsForChapter(courseChapterId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `institute-test/preloaded-tests-for-chapter`,
        { params: { courseChapterId: courseChapterId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPreloadedTestsForStudent(
    userId: number,
    courseId: number,
    classId: number,
    subjectId: number | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PreloadedTestInfo>>(
        `self-test/preloaded-tests-for-student`,
        { params: { userId: userId, courseId: courseId, classId: classId, subjectId: subjectId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPreloadedTestById(preloadedTestId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PreloadedTestInfo>(
        `institute-test/preloaded-test-by-id`,
        { params: { preloadedTestId: preloadedTestId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createSelfTestFromPreloadedTest(
    userId: number,
    preloadedTestId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(
        `self-test/create-self-test-from-preloaded-test`,
        { userId: userId, preloadedTestId: preloadedTestId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
  async resetPreloadedTestForStudent(
    userId: number,
    preloadedTestId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `self-test/reset-preloaded-test-for-student`,
        {
          params: {
            userId: userId,
            preloadedTestId: preloadedTestId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPastYearQuestionPapersForStudent(
    userId: number,
    courseId: number,
    subjectId: number | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PastYearQuestionPaper>>(
        `past-year-paper/past-year-question-papers-for-student`,
        { params: { userId: userId, courseId: courseId, subjectId: subjectId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createSelfTestFromPastYearPaper(
    userId: number,
    pastYearQuestionPaperId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(
        `past-year-paper/create-self-test-from-past-year-paper`,
        { userId: userId, pastYearQuestionPaperId: pastYearQuestionPaperId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async resetPastYearPaperForStudent(
    userId: number,
    pastYearQuestionPaperId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `self-test/reset-past-year-paper-for-student`,
        {
          params: {
            userId: userId,
            pastYearQuestionPaperId: pastYearQuestionPaperId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async migrateMultipleNewTestsToPreloaded(
    instituteTestIds: Array<number> | null,
    preloadedCategoryId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/migrate-multiple-new-tests-to-preloaded`,
        {
          instituteTestIds: instituteTestIds,
          preloadedCategoryId: preloadedCategoryId,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPastYearPapers(onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `past-year-paper/get-past-year-papers`,
        { params: { onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedPastYearPaper(pastYearPaperId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PastYearPaper>(
        `past-year-paper/get-detailed-past-year-paper`,
        { params: { pastYearPaperId: pastYearPaperId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPastYearQuestionPapers(
    pastYearPaperId: number,
    onlyActive: boolean,
    onlyUntagged: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `past-year-paper/get-past-year-question-papers`,
        {
          params: {
            pastYearPaperId: pastYearPaperId,
            onlyActive: onlyActive,
            onlyUntagged: onlyUntagged,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedPastYearQuestionPapers(
    pastYearPaperId: number,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `past-year-paper/detailed-past-year-question-papers`,
        { params: { pastYearPaperId: pastYearPaperId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveInstituteTestAsPastYearPaper(
    pastYearQuestionPaperId: number,
    instituteTestId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `past-year-paper/save-institute-test-as-past-year-paper`,
        {
          pastYearQuestionPaperId: pastYearQuestionPaperId,
          instituteTestId: instituteTestId,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createPastYearPaper(pastYearPaperInputs: PastYearPaperInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `past-year-paper/create-past-year-paper`,
        pastYearPaperInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updatePastYearPaper(pastYearPaperInputs: PastYearPaperUpdate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `past-year-paper/update-past-year-paper`,
        pastYearPaperInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createPastYearQuestionPaper(
    pastYearQuestionPaperInputs: PastYearQuestionPaperInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `past-year-paper/create-past-year-question-paper`,
        pastYearQuestionPaperInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updatePastYearQuestionPaper(
    pastYearQuestionPaperInputs: PastYearQuestionPaperUpdate,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `past-year-paper/update-past-year-question-paper`,
        pastYearQuestionPaperInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async publishPreloadedTest(
    testId: number,
    affiliationId: number,
    testName: string,
    batchIds: Array<number>,
    startDatetime: string,
    endDatetime: string,
    teachingAssessmentId: number | null,
    onlyOffline: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `institute-test/publish-preloaded-test`,
        {
          testId: testId,
          affiliationId: affiliationId,
          testName: testName,
          batchIds: batchIds,
          startDatetime: startDatetime,
          endDatetime: endDatetime,
          teachingAssessmentId: teachingAssessmentId,
          onlyOffline: onlyOffline,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updatePreloadedTests(
    preloadedTestCategoryId: number,
    preloadedTestIds: Array<number>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `institute-test/update-preloaded-tests`,
        { preloadedTestCategoryId: preloadedTestCategoryId, preloadedTestIds: preloadedTestIds },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deletePreloadedTests(preloadedTestIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<Array<number>>(
        `institute-test/delete-preloaded-tests`,
        { data: { preloadedTestIds: preloadedTestIds } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStandardTestTemplatesForCourse(courseId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<InstituteTestTemplate>>(
        `institute-test/standard-test-templates-for-course`,
        { params: { courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSavedTestTemplatesForCourse(
    courseId: number,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<InstituteTestTemplate>>(
        `institute-test/saved-test-templates-for-course`,
        { params: { courseId: courseId, affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllTestsSummaryForBatch(batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<BatchInstituteTestSummary>>(
        `affiliation-report/get-all-tests-summary-for-batch`,
        { params: { batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getInstituteTestPerformaceForStudent(
    userId: number,
    batchId: number,
    subjectId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudentInstituteTestSummary>>(
        `affiliation-report/get-institute-test-performance-for-student`,
        { params: { userId: userId, batchId: batchId, subjectId: subjectId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllTestsStudentMarksForBatch(batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AllInstituteTestStudentMarks>(
        `affiliation-report/get-all-test-student-marks-for-batch`,
        { params: { batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async generateManualMarksPdfReport(
    instituteTestId: number,
    batchId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(
        `institute-test/create-test-manual-marks-pdf-report`,
        { instituteTestId: instituteTestId, batchId: batchId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async generateManualMarksheet(instituteTestId: number, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<string>(
        `institute-test/get-test-manual-marks-marksheet`,
        { params: { instituteTestId: instituteTestId, batchId: batchId } },
      );

      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createTestSeries(testSeriesInputs: TestSeriesInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `test-series/create-test-series`,
        testSeriesInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTestSeries(testSeriesInputs: TestSeriesUpdate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `test-series/update-test-series`,
        testSeriesInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllTestSeries(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<InputOption>>(
        `test-series/get-all-test-series`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTestSeriesDetails(mongoId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<TestSeries>(
        `test-series/get-test-series-details`,
        { params: { mongoId: mongoId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addCourseToTestSeries(mongoId: string, courseIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `test-series/add-courses-to-test-series`,
        { mongoId: mongoId, courseIds: courseIds },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addTestPaperTemplateToTestPaper(
    testPaperInputs: TestPaperTemplateInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `test-series/add-test-paper-template-to-test-paper`,
        testPaperInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTestSeriesTestTemplates(courseId: number, affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<InstituteTestTemplateCreate>>(
        `test-series/test-series-test-templates-for-course`,
        { params: { courseId: courseId, affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteTestPaperTemplate(
    mongoId: string,
    testPaperId: string,
    courseId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `test-series/delete-test-paper-template`,
        { params: { mongoId: mongoId, testPaperId: testPaperId, courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteTestPaper(mongoId: string, testPaperId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(`test-series/delete-test-paper`, {
        params: { mongoId: mongoId, testPaperId: testPaperId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteSetFromTestPaperTemplate(
    mongoId: string,
    testPaperId: string,
    courseId: number,
    instituteTestId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `test-series/delete-set-from-test-paper-template`,
        {
          params: {
            mongoId: mongoId,
            testPaperId: testPaperId,
            courseId: courseId,
            instituteTestId: instituteTestId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addAffiliationsToTestSeries(
    mongoId: string,
    affiliationIds: Array<number>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `test-series/add-affiliations-to-test-series`,
        { mongoId: mongoId, affiliationIds: affiliationIds },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async mapInstituteTestToTestPaper(
    mongoId: string,
    testPaperId: string,
    courseId: number,
    instituteTestId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `test-series/map-institute-test-to-test-paper`,
        {
          mongoId: mongoId,
          testPaperId: testPaperId,
          courseId: courseId,
          instituteTestId: instituteTestId,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTestSeriesForBatch(mongoId: string, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<TestSeriesBatch>(
        `test-series/get-test-series-for-batch`,
        { params: { mongoId: mongoId, batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async publishTestPaperForBatch(
    mongoId: string,
    instituteTestId: number,
    batchIds: Array<number>,
    startDatetime: string,
    endDatetime: string,
    onlyOffline: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `test-series/publish-test-paper-for-batch`,
        {
          mongoId: mongoId,
          instituteTestId: instituteTestId,
          batchIds: batchIds,
          startDatetime: startDatetime,
          endDatetime: endDatetime,
          onlyOffline: onlyOffline,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTestSeriesSchoolTestReport(
    affiliationId: number,
    batchId: number,
    instituteTestIds: Array<number>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<PdfReportDisplay>(
        `test-series/get-test-series-school-test-report`,
        { affiliationId: affiliationId, instituteTestIds: instituteTestIds, batchId: batchId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
